import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { activitiesAPI, bookingsAPI, promoCodesAPI, giftCardsAPI, creditsAPI, resolveImageUrl } from '@/lib/api';
import axios from 'axios';
import { useAuth } from '@/App';
import { useTranslation } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Clock, Tag, Gift, Loader2, Check, CreditCard, Store, Minus, Plus, Coins, Shield, FileText, ChevronDown, ChevronUp, ScrollText, User, Mail, Lock, Phone } from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

export default function BookingPage() {
  const { activityId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const promoId = searchParams.get('promo');
  const urlDate = searchParams.get('date');
  const urlTime = searchParams.get('time');
  const urlDuration = searchParams.get('duration');
  const { user, login } = useAuth();
  const { t, lang } = useTranslation();

  const [activity, setActivity] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState({});
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [flashPromo, setFlashPromo] = useState(null);
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCardBalance, setGiftCardBalance] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [confirmedBookingId, setConfirmedBookingId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('online');
  const [outOfSeason, setOutOfSeason] = useState(false);
  const [blockedInfo, setBlockedInfo] = useState(null);
  const [creditBalance, setCreditBalance] = useState(null);

  // CGV acceptance state
  const [showCGV, setShowCGV] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [cgvText, setCgvText] = useState('');
  const [loadingCGV, setLoadingCGV] = useState(false);

  // Guest registration fields (shown when not logged in)
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestPassword, setGuestPassword] = useState('');

  const gtmDebutFiredRef = useRef(false);

  useEffect(() => {
    activitiesAPI.getOne(activityId).then(r => {
      setActivity(r.data);
      // GTM: user has arrived on the booking funnel entry point (fire once per activity)
      if (!gtmDebutFiredRef.current) {
        gtmDebutFiredRef.current = true;
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'debut_reservation',
            activity_id: r.data.id,
            activity_name: r.data.name,
            site_id: r.data.site_id,
          });
        } catch(e) { /* GTM optional */ }
      }
      const durations = r.data.durations || [];
      if (urlDuration && durations.length > 0) {
        const match = durations.find(d => d.duration_minutes === parseInt(urlDuration));
        setSelectedDuration(match || durations[0]);
      } else if (durations.length > 0) {
        setSelectedDuration(durations[0]);
      }
      if (urlDate && !promoId) {
        setSelectedDate(new Date(urlDate + 'T00:00:00'));
      }
    }).catch(() => navigate('/'));
  }, [activityId, navigate]);

  // Load credit balance once user is hydrated AND activity is loaded
  useEffect(() => {
    if (!user || !activity) return;
    creditsAPI.getBalance().then(res => {
      const siteCredits = res.data.find(b => b.site_id === activity.site_id);
      if (siteCredits && siteCredits.balance > 0) setCreditBalance(siteCredits);
      else setCreditBalance(null);
    }).catch(() => {});
  }, [user, activity]);

  // Load CGV text
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cgv`)
      .then(r => setCgvText(r.data.cgv_text || ''))
      .catch(() => {});
  }, []);

  const fetchSlots = useCallback(async () => {
    if (!selectedDate || !selectedDuration) return;
    setLoadingSlots(true);
    setOutOfSeason(false);
    setBlockedInfo(null);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const res = await bookingsAPI.getSlots(activityId, dateStr, selectedDuration.duration_minutes);
      if (res.data.out_of_season) {
        setAvailableSlots([]);
        setOutOfSeason(true);
      } else if (res.data.blocked) {
        setAvailableSlots([]);
        setBlockedInfo(res.data.block_reason || '');
      } else {
        setAvailableSlots(res.data.slots);
        setOutOfSeason(false);
      }
      setSelectedSlots({});
    } catch {
      setAvailableSlots([]);
    }
    setLoadingSlots(false);
  }, [selectedDate, selectedDuration, activityId]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  useEffect(() => {
    if (!promoId) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${API_URL}/api/flash-promos/${promoId}`)
      .then(r => {
        const p = r.data;
        setFlashPromo(p);
        setSelectedDate(new Date(p.date + 'T00:00:00'));
        const dur = activity?.durations?.find(d => d.duration_minutes === p.duration_minutes);
        if (dur) setSelectedDuration(dur);
      })
      .catch(() => {});
  }, [promoId, activity]);

  useEffect(() => {
    if (availableSlots.length > 0) {
      const targetTime = flashPromo?.time_slot || urlTime;
      if (targetTime) {
        const match = availableSlots.find(s => (typeof s === 'string' ? s : s.time) === targetTime);
        if (match) setSelectedSlots({ [targetTime]: 1 });
      }
    }
  }, [flashPromo, availableSlots, urlTime]);

  const handleSlotClick = (time, remaining) => {
    setSelectedSlots(prev => {
      const current = prev[time] || 0;
      if (current >= remaining) {
        const { [time]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [time]: current + 1 };
    });
  };

  const handleSlotIncrement = (time, remaining) => {
    setSelectedSlots(prev => {
      const current = prev[time] || 0;
      if (current >= remaining) return prev;
      return { ...prev, [time]: current + 1 };
    });
  };

  const handleSlotDecrement = (time) => {
    setSelectedSlots(prev => {
      const current = prev[time] || 0;
      if (current <= 1) {
        const { [time]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [time]: current - 1 };
    });
  };

  const totalQuantity = Object.values(selectedSlots).reduce((a, b) => a + b, 0);

  const validatePromo = async () => {
    if (!promoCode || !selectedDuration) return;
    try {
      const res = await promoCodesAPI.validate({
        code: promoCode,
        amount: selectedDuration.price,
        site_id: activity?.site_id,
        activity_id: activityId,
        activity_date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined
      });
      setPromoDiscount(res.data);
      toast.success(`Code promo applique : -${res.data.discount_amount}EUR`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code promo invalide');
      setPromoDiscount(null);
    }
  };

  const validateGiftCard = async () => {
    if (!giftCardCode) return;
    try {
      const res = await giftCardsAPI.validate(giftCardCode);
      setGiftCardBalance(res.data);
      toast.success(`Carte cadeau valide : ${res.data.balance}EUR disponible`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Carte cadeau invalide');
      setGiftCardBalance(null);
    }
  };

  const getSubtotal = () => {
    if (!selectedDuration) return 0;
    return selectedDuration.price * totalQuantity;
  };

  const getTotal = () => {
    if (!selectedDuration) return 0;
    let total = 0;
    for (const [time, qty] of Object.entries(selectedSlots)) {
      let slotPrice = selectedDuration.price;
      if (flashPromo && flashPromo.activity_id === activityId && time === flashPromo.time_slot) {
        slotPrice = flashPromo.discounted_price;
      }
      if (promoDiscount) {
        slotPrice = promoDiscount.final_amount;
      }
      total += slotPrice * qty;
    }
    if (giftCardBalance) total = Math.max(0, total - giftCardBalance.balance);
    return Math.max(0, Math.round(total * 100) / 100);
  };

  const getGiftCardDeduction = () => {
    if (!selectedDuration || !giftCardBalance) return 0;
    let total = 0;
    for (const [time, qty] of Object.entries(selectedSlots)) {
      let slotPrice = selectedDuration.price;
      if (promoDiscount) slotPrice = promoDiscount.final_amount;
      total += slotPrice * qty;
    }
    return Math.min(giftCardBalance.balance, total);
  };

  const handleBooking = async () => {
    if (!selectedDate || !selectedDuration || totalQuantity === 0) {
      toast.error('Veuillez selectionner au moins un creneau');
      return;
    }
    if (!cgvAccepted) {
      toast.error('Veuillez accepter les Conditions Generales de Vente');
      setShowCGV(true);
      return;
    }
    // Validate guest fields if not logged in
    if (!user) {
      if (!guestName || !guestEmail || !guestPassword) {
        toast.error('Veuillez renseigner votre nom, email et mot de passe');
        return;
      }
      if (guestPassword.length < 6) {
        toast.error('Le mot de passe doit contenir au moins 6 caracteres');
        return;
      }
    }
    setSubmitting(true);
    try {
      const slots = Object.entries(selectedSlots)
        .filter(([, qty]) => qty > 0)
        .map(([time_slot, quantity]) => ({ time_slot, quantity }));

      const bookingData = {
        activity_id: activityId,
        site_id: activity.site_id,
        date: format(selectedDate, 'yyyy-MM-dd'),
        slots,
        duration_minutes: selectedDuration.duration_minutes,
        origin_url: window.location.origin,
        payment_method: paymentMethod,
        flash_promo_id: flashPromo ? flashPromo.id : null,
        contract_signed: false,
        cgv_accepted: true,
      };

      // Add guest registration data if not logged in
      if (!user) {
        bookingData.guest_name = guestName;
        bookingData.guest_email = guestEmail;
        bookingData.guest_phone = guestPhone;
        bookingData.guest_password = guestPassword;
      }

      if (giftCardBalance && giftCardCode) {
        bookingData.gift_card_code = giftCardCode;
      }

      const res = await bookingsAPI.createMulti(bookingData);

      // Auto-login if a new account was created (guest checkout)
      if (res.data.token && res.data.user) {
        await login(res.data.token, res.data.user);
      }

      if (res.data.payment_method === 'onsite' || res.data.payment_method === 'gift_card' || res.data.payment_method === 'credits') {
        setConfirmedBookingId(res.data.booking_id);
        setBookingConfirmed(true);
        // GTM: reservation confirmed immediately (no online payment step)
        try {
          const amount = res.data.price != null ? Number(res.data.price) : Number(getTotal());
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'reservation_confirmee',
            value: amount,
            currency: 'EUR',
            transaction_id: res.data.booking_id,
            payment_method: res.data.payment_method,
            activity_id: activityId,
            activity_name: activity.name,
            site_id: activity.site_id,
          });
        } catch(e) { /* GTM optional */ }
      } else {
        // GTM: user is about to be redirected to online payment (Stripe/Revolut)
        try {
          const totalAmount = getTotal();
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'paiement_initie',
            value: Number(totalAmount),
            currency: 'EUR',
            transaction_id: res.data.booking_id,
            payment_provider: res.data.payment_provider || 'stripe',
            activity_id: activityId,
            activity_name: activity.name,
            site_id: activity.site_id,
          });
        } catch(e) { /* GTM optional */ }
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la reservation');
      setSubmitting(false);
    }
  };

  if (!activity) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
      <Loader2 className="w-8 h-8 animate-spin text-[#00FF00]" />
    </div>
  );

  if (bookingConfirmed) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0A0A0A]" data-testid="booking-confirmation-overlay">
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="absolute rounded-full animate-float"
            style={{
              width: `${6 + Math.random() * 12}px`, height: `${6 + Math.random() * 12}px`,
              background: i % 3 === 0 ? '#00FF00' : i % 3 === 1 ? '#FF6600' : '#33CCFF',
              opacity: 0.15 + Math.random() * 0.3,
              left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`, animationDuration: `${3 + Math.random() * 4}s`,
            }}
          />
        ))}
      </div>
      <div className="relative text-center px-6 animate-fadeInUp">
        <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-[#00FF00]/15 border-2 border-[#00FF00] flex items-center justify-center animate-scaleIn">
          <svg className="w-12 h-12 text-[#00FF00]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 13l4 4L19 7" className="animate-drawCheck" style={{strokeDasharray: 30, strokeDashoffset: 30, animation: 'drawCheck 0.5s 0.4s forwards'}} />
          </svg>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">Felicitations !</h1>
        <p className="text-lg text-[#00FF00] font-semibold mb-2">Votre reservation est confirmee</p>
        <p className="text-slate-400 text-sm mb-8 max-w-md mx-auto">
          {activity?.name}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate(`/booking/${confirmedBookingId}/participants`)}
            className="px-6 py-3 rounded-xl bg-[#FF6600] text-white font-bold text-sm hover:bg-[#E55A00] transition-colors animate-pulse-soft"
            data-testid="confirm-register-participants-btn"
          >
            Enregistrer les participants
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 rounded-xl bg-[#1A1A1A] border border-[#2A2A2A] text-slate-300 font-medium text-sm hover:border-[#00FF00]/30 hover:text-white transition-colors"
            data-testid="confirm-go-dashboard-btn"
          >
            Voir mes reservations
          </button>
        </div>
      </div>
      <style>{`
        @keyframes float { 0%,100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-30px) scale(1.2); } }
        @keyframes fadeInUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
        @keyframes scaleIn { from { opacity:0; transform:scale(0.3); } to { opacity:1; transform:scale(1); } }
        @keyframes drawCheck { to { stroke-dashoffset: 0; } }
        @keyframes pulse-soft { 0%,100% { box-shadow: 0 0 0 0 rgba(255,102,0,0.5); } 50% { box-shadow: 0 0 20px 4px rgba(255,102,0,0.3); } }
        .animate-float { animation: float 4s ease-in-out infinite; }
        .animate-fadeInUp { animation: fadeInUp 0.6s ease-out; }
        .animate-scaleIn { animation: scaleIn 0.5s cubic-bezier(0.175,0.885,0.32,1.275); }
        .animate-pulse-soft { animation: pulse-soft 2s ease-in-out infinite; }
      `}</style>
    </div>
  );

  const guestFieldsValid = user || (guestName && guestEmail && guestPassword && guestPassword.length >= 6);
  const canBook = selectedDate && selectedDuration && totalQuantity > 0 && cgvAccepted && guestFieldsValid &&
    !(paymentMethod === 'credits' && creditBalance && getTotal() > creditBalance.balance);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="booking-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-[#00FF00] text-sm mb-8 transition-colors"
          data-testid="booking-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour aux activites
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Activity Info + Calendar */}
          <div className="lg:col-span-2 space-y-8">
            {/* Activity Header */}
            <div className="relative rounded-2xl overflow-hidden h-64">
              <img src={resolveImageUrl(activity.image_url)} alt={activity.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
              <div className="absolute bottom-0 p-6">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-white" data-testid="booking-activity-name">
                  {activity.name}
                </h1>
                <p className="text-white/60 mt-1">{activity.description}</p>
                {activity.available_from && activity.available_to && (
                  <p className="text-xs text-orange-400/80 mt-2 flex items-center gap-1" data-testid="season-info">
                    <Clock className="w-3 h-3" />
                    {lang === 'fr' ? 'Saison' : 'Season'}: {activity.available_from.slice(8)}/{activity.available_from.slice(5,7)} - {activity.available_to.slice(8)}/{activity.available_to.slice(5,7)}
                  </p>
                )}
              </div>
            </div>

            {/* Duration Selection */}
            <div>
              <h3 className="font-heading text-lg font-bold text-white mb-4">Choisissez la duree</h3>
              <div className="grid grid-cols-2 gap-4">
                {activity.durations.map((dur) => (
                  <button
                    key={dur.duration_minutes}
                    onClick={() => { setSelectedDuration(dur); setPromoDiscount(null); setSelectedSlots({}); }}
                    className={`p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                      selectedDuration?.duration_minutes === dur.duration_minutes
                        ? 'border-[#00FF00] bg-[#00FF00]/5 shadow-[0_0_20px_rgba(0,255,0,0.15)]'
                        : 'border-[#2A2A2A] bg-[#1A1A1A] hover:border-[#333]'
                    }`}
                    data-testid={`duration-${dur.duration_minutes}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-[#00FF00]" />
                      <span className="font-semibold text-white">{dur.label}</span>
                    </div>
                    <span className="font-heading text-2xl font-bold text-[#00FF00]">{dur.price}EUR</span>
                    <span className="text-xs text-slate-500 ml-1">/ place</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Calendar */}
            <div>
              <h3 className="font-heading text-lg font-bold text-white mb-4">Choisissez la date</h3>
              <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-4 shadow-float inline-block">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(d) => { setSelectedDate(d); setSelectedSlots({}); }}
                  disabled={(date) => {
                    const now = new Date();
                    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    return date < today;
                  }}
                  locale={fr}
                  className="booking-calendar"
                  data-testid="booking-calendar"
                />
              </div>
            </div>

            {/* Time Slots */}
            {selectedDate && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-lg font-bold text-white">
                    Creneaux disponibles - {format(selectedDate, 'dd MMMM yyyy', { locale: fr })}
                  </h3>
                  {totalQuantity > 0 && (
                    <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-[#00FF00]/30 text-sm px-3 py-1" data-testid="total-places-badge">
                      {totalQuantity} place{totalQuantity > 1 ? 's' : ''} selectionnee{totalQuantity > 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500 mb-4">
                  {lang === 'fr' ? 'Cliquez sur un creneau pour ajouter une place. Vous pouvez selectionner plusieurs creneaux.' : 'Click a slot to add a place. You can select multiple slots.'}
                </p>
                {loadingSlots ? (
                  <div className="flex items-center gap-2 text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Chargement...
                  </div>
                ) : outOfSeason ? (
                  <div className="bg-[#1A1A1A] border border-orange-500/30 rounded-xl p-4 text-center" data-testid="out-of-season-msg">
                    <p className="text-orange-400 font-semibold text-sm mb-1">
                      {lang === 'fr' ? 'Activite hors saison' : 'Activity out of season'}
                    </p>
                    <p className="text-slate-500 text-xs">
                      {activity?.available_from && activity?.available_to
                        ? (lang === 'fr'
                            ? `Disponible du ${activity.available_from.slice(8)}/${activity.available_from.slice(5,7)} au ${activity.available_to.slice(8)}/${activity.available_to.slice(5,7)}`
                            : `Available from ${activity.available_from.slice(5)} to ${activity.available_to.slice(5)}`)
                        : (lang === 'fr' ? 'Non disponible a cette date' : 'Not available on this date')}
                    </p>
                  </div>
                ) : blockedInfo !== null ? (
                  <div className="bg-[#1A1A1A] border border-red-500/30 rounded-xl p-4 text-center" data-testid="blocked-date-msg">
                    <p className="text-red-400 font-semibold text-sm mb-1">
                      {lang === 'fr' ? 'Date indisponible' : 'Date unavailable'}
                    </p>
                    <p className="text-slate-500 text-xs">
                      {blockedInfo
                        ? blockedInfo
                        : (lang === 'fr' ? 'Cette date est fermee. Veuillez choisir une autre date.' : 'This date is closed. Please choose another date.')}
                    </p>
                  </div>
                ) : availableSlots.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="time-slots-grid">
                    {availableSlots.map((slot) => {
                      const time = typeof slot === 'string' ? slot : slot.time;
                      const remaining = typeof slot === 'string' ? null : slot.remaining;
                      const selected = selectedSlots[time] || 0;
                      const isFull = remaining !== null && remaining === 0;

                      // Hide slots less than 2 hours from now (same day only)
                      if (selectedDate) {
                        const now = new Date();
                        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                        const selDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
                        if (selDay.getTime() === today.getTime()) {
                          const [h, m] = time.split(':').map(Number);
                          const slotTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m);
                          if (slotTime - now < 2 * 60 * 60 * 1000) return null;
                        }
                      }

                      return (
                        <div
                          key={time}
                          className={`rounded-xl transition-all duration-200 overflow-hidden ${
                            isFull
                              ? 'bg-[#1A1A1A] border border-[#1A1A1A] opacity-40'
                              : selected > 0
                                ? 'bg-[#00FF00]/10 border-2 border-[#00FF00] shadow-[0_0_20px_rgba(0,255,0,0.15)]'
                                : 'bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#00FF00]/50'
                          }`}
                          data-testid={`time-slot-${time}`}
                        >
                          <button
                            onClick={() => !isFull && handleSlotClick(time, remaining)}
                            disabled={isFull}
                            className="w-full py-3 px-4 text-center cursor-pointer disabled:cursor-not-allowed"
                          >
                            <span className={`text-sm font-bold ${selected > 0 ? 'text-[#00FF00]' : isFull ? 'text-slate-700' : 'text-white'}`}>
                              {time}
                            </span>
                            {remaining !== null && (
                              <span className={`block text-[10px] mt-0.5 ${
                                isFull ? 'text-red-500/60' : remaining <= 2 ? 'text-orange-400' : 'text-slate-500'
                              }`}>
                                {remaining} place{remaining > 1 ? 's' : ''} dispo
                              </span>
                            )}
                          </button>

                          {selected > 0 && (
                            <div className="flex items-center justify-center gap-3 pb-3 px-3">
                              <button
                                onClick={() => handleSlotDecrement(time)}
                                className="w-7 h-7 rounded-lg bg-[#0A0A0A] border border-[#2A2A2A] flex items-center justify-center text-white hover:border-[#00FF00] hover:text-[#00FF00] transition-colors"
                                data-testid={`slot-minus-${time}`}
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="font-heading text-lg font-bold text-[#00FF00] w-6 text-center" data-testid={`slot-count-${time}`}>
                                {selected}
                              </span>
                              <button
                                onClick={() => handleSlotIncrement(time, remaining)}
                                disabled={remaining !== null && selected >= remaining}
                                className="w-7 h-7 rounded-lg bg-[#0A0A0A] border border-[#2A2A2A] flex items-center justify-center text-white hover:border-[#00FF00] hover:text-[#00FF00] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                data-testid={`slot-plus-${time}`}
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">Aucun creneau disponible pour cette date.</p>
                )}
              </div>
            )}
          </div>

          {/* Right: Summary Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 glass-card rounded-2xl p-6 shadow-glow space-y-6" data-testid="booking-summary">
              <h3 className="font-heading text-lg font-bold text-white">{t('booking.summary')}</h3>

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Activite</span>
                  <span className="font-semibold text-white">{activity.name}</span>
                </div>
                {selectedDuration && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Duree</span>
                    <span className="font-semibold text-white">{selectedDuration.label}</span>
                  </div>
                )}
                {selectedDate && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Date</span>
                    <span className="font-semibold text-white">{format(selectedDate, 'dd/MM/yyyy')}</span>
                  </div>
                )}

                {Object.keys(selectedSlots).length > 0 && (
                  <div className="border-t border-[#2A2A2A] pt-3 space-y-2">
                    <span className="text-xs text-slate-500 uppercase tracking-wider font-bold">Creneaux selectionnes</span>
                    {Object.entries(selectedSlots).sort(([a], [b]) => a.localeCompare(b)).map(([time, qty]) => (
                      <div key={time} className="flex justify-between text-sm bg-[#0A0A0A] rounded-lg px-3 py-2" data-testid={`summary-slot-${time}`}>
                        <span className="text-[#00FF00] font-semibold">{time}</span>
                        <span className="text-white">{qty} place{qty > 1 ? 's' : ''} — {(selectedDuration.price * qty).toFixed(2)}EUR</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-[#2A2A2A] pt-4 space-y-4">
                {/* Promo Code */}
                <div>
                  <Label className="text-sm font-semibold text-slate-300 flex items-center gap-1 mb-2">
                    <Tag className="w-3 h-3" /> {t('booking.promo_code')}
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      placeholder="BIENVENUE10"
                      className="h-10 bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                      data-testid="promo-code-input"
                    />
                    <Button
                      variant="outline"
                      onClick={validatePromo}
                      className="h-10 px-4 border-[#00FF00] text-[#00FF00] hover:bg-[#00FF00] hover:text-black"
                      data-testid="apply-promo-btn"
                    >
                      OK
                    </Button>
                  </div>
                  {promoDiscount && (
                    <p className="text-xs text-[#00FF00] mt-1 flex items-center gap-1">
                      <Check className="w-3 h-3" /> -{promoDiscount.discount_amount}EUR applique
                    </p>
                  )}
                </div>

                {/* Gift Card */}
                <div>
                  <Label className="text-sm font-semibold text-slate-300 flex items-center gap-1 mb-2">
                    <Gift className="w-3 h-3" /> {t('booking.gift_card')}
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={giftCardCode}
                      onChange={(e) => setGiftCardCode(e.target.value)}
                      placeholder="RW-XXXX-XXXX-XXXX"
                      className="h-10 bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                      data-testid="gift-card-input"
                    />
                    <Button
                      variant="outline"
                      onClick={validateGiftCard}
                      className="h-10 px-4 border-[#00FF00] text-[#00FF00] hover:bg-[#00FF00] hover:text-black"
                      data-testid="apply-gift-card-btn"
                    >
                      OK
                    </Button>
                  </div>
                  {giftCardBalance && (
                    <p className="text-xs text-[#00FF00] mt-1 flex items-center gap-1">
                      <Check className="w-3 h-3" /> Solde : {giftCardBalance.balance}EUR
                    </p>
                  )}
                </div>
              </div>

              {/* Total */}
              <div className="border-t border-[#2A2A2A] pt-4">
                {totalQuantity > 1 && selectedDuration && (
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-500">Sous-total ({totalQuantity} places)</span>
                    <span className={promoDiscount ? 'line-through text-slate-600' : 'text-slate-300'}>{getSubtotal().toFixed(2)}EUR</span>
                  </div>
                )}
                {giftCardBalance && getGiftCardDeduction() > 0 && (
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-500">Carte cadeau</span>
                    <span className="text-[#00FF00]">-{getGiftCardDeduction().toFixed(2)}EUR</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="font-heading text-lg font-bold text-white">Total</span>
                  <span className="font-heading text-2xl font-bold text-[#00FF00]">{getTotal().toFixed(2)}EUR</span>
                </div>
                {giftCardBalance && getTotal() <= 0 && (
                  <p className="text-xs text-[#00FF00] mt-2 text-center font-semibold">Entierement couvert par la carte cadeau !</p>
                )}
              </div>

              {/* Payment Method Selection */}
              <div className="border-t border-[#2A2A2A] pt-4">
                <Label className="text-sm font-semibold text-slate-300 mb-3 block">{lang === 'fr' ? 'Mode de paiement' : 'Payment method'}</Label>
                <div className={`grid ${creditBalance ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
                  <button
                    onClick={() => setPaymentMethod('online')}
                    className={`p-3 rounded-xl border-2 text-center transition-all duration-200 ${
                      paymentMethod === 'online'
                        ? 'border-[#00FF00] bg-[#00FF00]/5 shadow-[0_0_15px_rgba(0,255,0,0.15)]'
                        : 'border-[#2A2A2A] bg-[#1A1A1A] hover:border-[#333]'
                    }`}
                    data-testid="payment-method-online"
                  >
                    <CreditCard className={`w-5 h-5 mx-auto mb-1.5 ${paymentMethod === 'online' ? 'text-[#00FF00]' : 'text-slate-500'}`} />
                    <span className={`text-xs font-semibold block ${paymentMethod === 'online' ? 'text-white' : 'text-slate-400'}`}>{t('booking.pay_online')}</span>
                    <span className="text-[10px] text-slate-600 block mt-0.5">Stripe securise</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('onsite')}
                    className={`p-3 rounded-xl border-2 text-center transition-all duration-200 ${
                      paymentMethod === 'onsite'
                        ? 'border-[#00FF00] bg-[#00FF00]/5 shadow-[0_0_15px_rgba(0,255,0,0.15)]'
                        : 'border-[#2A2A2A] bg-[#1A1A1A] hover:border-[#333]'
                    }`}
                    data-testid="payment-method-onsite"
                  >
                    <Store className={`w-5 h-5 mx-auto mb-1.5 ${paymentMethod === 'onsite' ? 'text-[#00FF00]' : 'text-slate-500'}`} />
                    <span className={`text-xs font-semibold block ${paymentMethod === 'onsite' ? 'text-white' : 'text-slate-400'}`}>{t('booking.pay_onsite')}</span>
                    <span className="text-[10px] text-slate-600 block mt-0.5">Au comptoir</span>
                  </button>
                  {creditBalance && (
                    <button
                      onClick={() => setPaymentMethod('credits')}
                      className={`p-3 rounded-xl border-2 text-center transition-all duration-200 ${
                        paymentMethod === 'credits'
                          ? 'border-[#00FF00] bg-[#00FF00]/5 shadow-[0_0_15px_rgba(0,255,0,0.15)]'
                          : 'border-[#2A2A2A] bg-[#1A1A1A] hover:border-[#333]'
                      }`}
                      data-testid="payment-method-credits"
                    >
                      <Coins className={`w-5 h-5 mx-auto mb-1.5 ${paymentMethod === 'credits' ? 'text-[#00FF00]' : 'text-slate-500'}`} />
                      <span className={`text-xs font-semibold block ${paymentMethod === 'credits' ? 'text-white' : 'text-slate-400'}`}>Credits</span>
                      <span className="text-[10px] text-[#00FF00] block mt-0.5">{creditBalance.balance}EUR dispo</span>
                    </button>
                  )}
                </div>
                {paymentMethod === 'credits' && creditBalance && getTotal() > creditBalance.balance && (
                  <p className="text-xs text-red-400 mt-2">Solde insuffisant ({creditBalance.balance}EUR disponibles, {getTotal().toFixed(2)}EUR requis)</p>
                )}
              </div>

              {/* Guest Registration Section (shown when not logged in) */}
              {!user && (
                <div className="border-t border-[#2A2A2A] pt-4" data-testid="guest-registration-section">
                  <Label className="text-sm font-semibold text-slate-300 mb-3 block flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-[#00FF00]" /> Vos informations
                  </Label>
                  <p className="text-xs text-slate-500 mb-3">Un compte sera cree automatiquement a la validation</p>
                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <User className="w-3 h-3 text-slate-500" />
                        <span className="text-xs font-semibold text-slate-400">Nom complet *</span>
                      </div>
                      <Input
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        placeholder="Jean Dupont"
                        className="h-10 bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                        data-testid="guest-name-input"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <Mail className="w-3 h-3 text-slate-500" />
                        <span className="text-xs font-semibold text-slate-400">Email *</span>
                      </div>
                      <Input
                        type="email"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        placeholder="votre@email.com"
                        className="h-10 bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                        data-testid="guest-email-input"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <Phone className="w-3 h-3 text-slate-500" />
                        <span className="text-xs font-semibold text-slate-400">Telephone</span>
                      </div>
                      <Input
                        value={guestPhone}
                        onChange={(e) => setGuestPhone(e.target.value)}
                        placeholder="+33 6 XX XX XX XX"
                        className="h-10 bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                        data-testid="guest-phone-input"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <Lock className="w-3 h-3 text-slate-500" />
                        <span className="text-xs font-semibold text-slate-400">Mot de passe *</span>
                      </div>
                      <Input
                        type="password"
                        value={guestPassword}
                        onChange={(e) => setGuestPassword(e.target.value)}
                        placeholder="Min. 6 caracteres"
                        className="h-10 bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                        data-testid="guest-password-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* CGV Acceptance Section */}
              <div className="border-t border-[#2A2A2A] pt-4" data-testid="cgv-section">
                <label className="flex items-start gap-3 cursor-pointer group" data-testid="cgv-checkbox-label">
                  <input
                    type="checkbox"
                    checked={cgvAccepted}
                    onChange={e => setCgvAccepted(e.target.checked)}
                    className="mt-0.5 accent-[#00FF00] w-4 h-4 flex-shrink-0"
                    data-testid="cgv-accept-checkbox"
                  />
                  <span className={`text-sm leading-relaxed transition-colors ${cgvAccepted ? 'text-white font-medium' : 'text-slate-400 group-hover:text-slate-200'}`}>
                    J'accepte les{' '}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); setShowCGV(!showCGV); }}
                      className="underline text-[#00FF00] hover:text-[#00CC00] font-medium"
                      data-testid="cgv-toggle-btn"
                    >
                      Conditions Generales de Vente
                    </button>
                  </span>
                </label>

                {showCGV && (
                  <div className="mt-3 bg-[#0A0A0A] rounded-xl p-4 max-h-72 overflow-y-auto border border-[#2A2A2A] animate-in slide-in-from-top-2 duration-200" data-testid="cgv-text-container">
                    <p className="text-xs text-slate-400 leading-relaxed whitespace-pre-line">{cgvText || 'Chargement...'}</p>
                  </div>
                )}
              </div>

              <Button
                onClick={handleBooking}
                disabled={!canBook || submitting}
                className="w-full btn-pill bg-[#00FF00] text-black hover:bg-[#00CC00] shadow-[0_0_30px_rgba(0,255,0,0.3)] text-base py-6 font-bold disabled:opacity-50"
                data-testid="confirm-booking-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {paymentMethod === 'onsite' || paymentMethod === 'credits' ? 'Reservation...' : 'Redirection...'}</>
                ) : paymentMethod === 'credits' ? (
                  `Reserver${totalQuantity > 0 ? ` ${totalQuantity} place${totalQuantity > 1 ? 's' : ''}` : ''} avec credits`
                ) : paymentMethod === 'onsite' ? (
                  `Reserver${totalQuantity > 0 ? ` ${totalQuantity} place${totalQuantity > 1 ? 's' : ''}` : ''} - Sur place`
                ) : (
                  `Payer${totalQuantity > 0 ? ` ${totalQuantity} place${totalQuantity > 1 ? 's' : ''}` : ''} et reserver`
                )}
              </Button>

              <p className="text-xs text-slate-600 text-center">
                {paymentMethod === 'credits'
                  ? `${getTotal().toFixed(2)}EUR seront deduits de votre solde credits`
                  : paymentMethod === 'onsite'
                  ? 'Le paiement sera effectue au comptoir le jour de la session'
                  : 'Paiement securise par Stripe'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
